import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useAuthStore from '@/store/authStore';
import { authAPI } from '@/services/api';

/**
 * Hook pour vérifier périodiquement la validité de la session
 * et déconnecter automatiquement l'utilisateur si la session a expiré
 *
 * @param {number} checkInterval - Intervalle de vérification en millisecondes (défaut: 15000 = 15 secondes)
 */
const useSessionCheck = (checkInterval = 15000) => {
  const { isAuthenticated, logout } = useAuthStore();
  const location = useLocation();
  const intervalRef = useRef(null);
  const isCheckingRef = useRef(false);
  const isRedirectingRef = useRef(false);
  const isAuthenticatedRef = useRef(isAuthenticated);

  // Mettre à jour la référence quand isAuthenticated change
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    // Pages publiques où la vérification n'est pas nécessaire
    const publicPaths = ['/auth', '/'];
    const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

    // Ne pas vérifier sur les pages publiques ou si l'utilisateur n'est pas authentifié
    if (isPublicPath || !isAuthenticated) {
      return;
    }

    // Fonction de vérification de session
    const checkSession = async () => {
      // Éviter les vérifications multiples simultanées
      if (isCheckingRef.current || isRedirectingRef.current) {
        return;
      }

      // Vérifier à nouveau si l'utilisateur est toujours authentifié
      if (!isAuthenticatedRef.current) {
        return;
      }

      isCheckingRef.current = true;

      try {
        // Vérifier si la session est toujours valide
        await authAPI.me();
        // Si la requête réussit, la session est valide
      } catch (error) {
        // Si 401 ou 403, vérifier si c'est vraiment une session expirée
        // Ne déconnecter que si l'utilisateur était authentifié
        if ((error.response?.status === 401 || error.response?.status === 403) && isAuthenticatedRef.current) {
          // Éviter les redirections multiples
          if (isRedirectingRef.current) {
            return;
          }
          isRedirectingRef.current = true;

          console.log('Session expirée détectée, déconnexion automatique...');

          // Nettoyer le store
          logout();

          // Stocker le message dans sessionStorage pour l'afficher après redirection
          sessionStorage.setItem('sessionExpiredMessage', 'true');

          // Rediriger vers la page de login avec le paramètre session_expired
          // Utiliser window.location pour forcer un rechargement complet et nettoyer l'état
          setTimeout(() => {
            window.location.href = '/auth?tab=login&session_expired=true';
          }, 100);
        }
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Vérifier immédiatement au montage
    checkSession();

    // Configurer la vérification périodique
    intervalRef.current = setInterval(checkSession, checkInterval);

    // Nettoyer l'intervalle lors du démontage
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, location.pathname, checkInterval, logout]);

  return null;
};

export default useSessionCheck;
